import type { NavItem } from "@/types/nav-item.types";

/**
 * Resolves the logo URL for the currently active nav item (or its active sub-item).
 * When `preferMobile` is true, uses `mobileLogoUrl` if the active item provides one.
 * Dedicated mobile logos skip `transform` (they are already theme-ready assets).
 */
export function getActiveNavLogoUrl(
    navItems: NavItem[],
    defaultLogoUrl: string,
    transform?: (url: string) => string,
    preferMobile = false,
): string {
    const apply = (url?: string, skipTransform = false): string => {
        if (!url) return defaultLogoUrl;
        return transform && !skipTransform ? transform(url) : url;
    };

    for (const item of navItems) {
        const activeSubItem = item.subItems?.find((sub) => sub.isActive);

        if (activeSubItem) {
            if (preferMobile) {
                const mobileUrl =
                    activeSubItem.mobileLogoUrl ?? item.mobileLogoUrl;
                if (mobileUrl) return apply(mobileUrl, true);
                return apply(activeSubItem.logoUrl ?? item.logoUrl);
            }
            return apply(activeSubItem.logoUrl ?? item.logoUrl);
        }

        if (item.isActive) {
            if (preferMobile && item.mobileLogoUrl) {
                return apply(item.mobileLogoUrl, true);
            }
            return apply(item.logoUrl);
        }
    }

    return defaultLogoUrl;
}

/** Prefer an in-app path so logo clicks stay on the current origin. */
function toAppPath(href: string): string {
    if (href.startsWith("/")) return href;
    try {
        const { pathname, search, hash } = new URL(href);
        return `${pathname}${search}${hash}` || "/";
    } catch {
        return href;
    }
}

/**
 * Resolves the logo click destination for the active product nav item.
 * Uses the same `isActive` + `logoUrl` signals as `getActiveNavLogoUrl`, so
 * whenever a page-specific logo is shown, click goes to that product listing.
 * Falls back to `defaultHref` (home / platform) when no product logo is active.
 */
export function getActiveNavLogoHref(
    navItems: NavItem[],
    defaultHref: string,
): string {
    for (const item of navItems) {
        const activeSubItem = item.subItems?.find((sub) => sub.isActive);

        if (activeSubItem && (activeSubItem.logoUrl || item.logoUrl)) {
            const href = activeSubItem.href || item.href;
            if (href) return toAppPath(href);
        }

        if (item.isActive && item.logoUrl && item.href) {
            return toAppPath(item.href);
        }
    }

    return defaultHref;
}
